/* ============================================================
   Quick Shop popup (snippets/quick-shop-modal.liquid).
   A product card's Quick Shop button carries data-quick-shop="<handle>".
   A plain left-click opens the popup instead of following the card
   link; ctrl/cmd/middle-click still open the product page. If the
   product can't be loaded, the click falls back to the product page.
   Add to Cart goes through window.Moonrise.cart (cart-store.js), so
   the cart drawer, header count and cart page all stay in sync.
   ============================================================ */

(function () {
  'use strict';

  if (window.Moonrise && window.Moonrise.quickShop) return;

  var dialog = document.getElementById('QuickShopModal');
  if (!dialog || typeof dialog.showModal !== 'function') return; // card links keep working

  var body = dialog.querySelector('[data-quick-shop-body]');
  var moneyFormat = dialog.getAttribute('data-money-format') || '${{amount}}';
  var LABEL_ADD = dialog.getAttribute('data-label-add') || 'Add to Cart';
  var LABEL_SOLD_OUT = dialog.getAttribute('data-label-sold-out') || 'Sold Out';
  var LABEL_UNAVAILABLE = dialog.getAttribute('data-label-unavailable') || 'Unavailable';
  var DEFAULT_LABEL = dialog.getAttribute('aria-label') || 'Quick shop';
  var CUSTOM_VALUE = 'Custom Size'; // fine jewelry: needs the size field on the product page
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var cache = {};
  var state = null;

  /* ---------- helpers ---------- */
  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function sized(url, width) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function formatMoney(cents) {
    var match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return moneyFormat;
    function delimit(precision, thousands, decimal) {
      var parts = (Number(cents || 0) / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }
    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = delimit(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = delimit(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = delimit(2, "'", '.'); break;
      default: value = delimit(2, ',', '.');
    }
    return moneyFormat.replace(match[0], value);
  }

  function optionName(option) {
    return typeof option === 'string' ? option : (option && option.name) || '';
  }

  function plainText(html, max) {
    var div = document.createElement('div');
    div.innerHTML = html || '';
    var text = (div.textContent || '').replace(/\s+/g, ' ').trim();
    if (text.length <= max) return text;
    return text.slice(0, max).replace(/\s+\S*$/, '') + '…';
  }

  function withVariant(url, id) {
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'variant=' + id;
  }

  function fetchProduct(handle) {
    if (cache[handle]) return Promise.resolve(cache[handle]);
    return fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (product) {
        cache[handle] = product;
        return product;
      });
  }

  /* ---------- open / close ---------- */
  function open(handle, url) {
    state = null;
    body.innerHTML = '<p class="quick-shop-loading" role="status">Loading…</p>';
    dialog.setAttribute('aria-label', DEFAULT_LABEL);
    document.documentElement.classList.add('quick-shop-open');
    dialog.showModal();
    fetchProduct(handle)
      .then(function (product) { render(product, url); })
      .catch(function () {
        dialog.close();
        window.location.href = url;
      });
  }

  dialog.addEventListener('close', function () {
    document.documentElement.classList.remove('quick-shop-open');
  });
  dialog.addEventListener('click', function (e) {
    if (e.target === dialog) dialog.close(); // backdrop
    if (e.target.closest('[data-quick-shop-close]')) dialog.close();
  });

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var trigger = e.target.closest('[data-quick-shop]');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    open(trigger.getAttribute('data-quick-shop'), trigger.getAttribute('data-product-url') || (root + 'products/' + trigger.getAttribute('data-quick-shop')));
  });

  /* ---------- render ---------- */
  function render(product, url) {
    var options = (product.options || []).map(optionName);
    var variants = product.variants || [];
    var hasOptions = !(variants.length === 1 && variants[0].title === 'Default Title');
    var start = variants.filter(function (v) { return v.available; })[0] || variants[0];
    state = { product: product, url: url, options: options, selected: start ? start.options.slice() : [] };

    var optionsHtml = '';
    if (hasOptions) {
      options.forEach(function (name, i) {
        var values = [];
        variants.forEach(function (v) {
          if (values.indexOf(v.options[i]) === -1) values.push(v.options[i]);
        });
        var imageFor = {};
        variants.forEach(function (v) {
          if (v.featured_image && !imageFor[v.options[i]]) imageFor[v.options[i]] = v.featured_image.src;
        });
        var swatches = /colou?r/i.test(name) && values.every(function (val) { return imageFor[val]; });

        optionsHtml += '<fieldset class="quick-shop-option"><legend class="quick-shop-option-label">' + esc(name) +
          ':<span data-qs-selected="' + i + '">' + esc(state.selected[i]) + '</span></legend><div class="quick-shop-values">';
        values.forEach(function (val, j) {
          var id = 'QuickShopOpt-' + i + '-' + j;
          optionsHtml += '<input type="radio" class="quick-shop-radio visually-hidden" name="qs-option-' + i + '" id="' + id +
            '" value="' + esc(val) + '" data-qs-option="' + i + '"' + (val === state.selected[i] ? ' checked' : '') + '>';
          if (swatches) {
            optionsHtml += '<label for="' + id + '" class="quick-shop-value quick-shop-swatch" title="' + esc(val) + '">' +
              '<img src="' + esc(sized(imageFor[val], 120)) + '" alt="" width="42" height="42" loading="lazy">' +
              '<span class="visually-hidden">' + esc(val) + '</span></label>';
          } else {
            optionsHtml += '<label for="' + id + '" class="quick-shop-value">' + esc(val) + '</label>';
          }
        });
        optionsHtml += '</div></fieldset>';
      });
    }

    var images = (product.images || []).slice(0, 8);
    var thumbsHtml = '';
    if (images.length > 1) {
      thumbsHtml = '<div class="quick-shop-thumbs">' + images.map(function (src, n) {
        return '<button type="button" class="quick-shop-thumb" data-qs-thumb="' + esc(src) + '" aria-label="Show image ' + (n + 1) + '">' +
          '<img src="' + esc(sized(src, 120)) + '" alt="" width="56" height="56" loading="lazy"></button>';
      }).join('') + '</div>';
    }

    var desc = plainText(product.description, 180);

    body.innerHTML =
      '<div class="quick-shop-grid">' +
        '<div class="quick-shop-media">' +
          '<div class="quick-shop-main"><img data-qs-image src="" alt="" width="800" height="800"></div>' +
          thumbsHtml +
        '</div>' +
        '<div class="quick-shop-info">' +
          '<h2 class="quick-shop-title" id="QuickShopTitle">' + esc(product.title) + '</h2>' +
          '<div class="quick-shop-price"><span data-qs-price></span><s data-qs-compare hidden></s></div>' +
          (desc ? '<p class="quick-shop-desc">' + esc(desc) + '</p>' : '') +
          '<form class="quick-shop-form" data-qs-form novalidate>' +
            optionsHtml +
            '<div class="quick-shop-buy">' +
              '<div class="quick-shop-qty" role="group" aria-label="Quantity">' +
                '<button type="button" data-qs-step="-1" aria-label="Decrease quantity">−</button>' +
                '<input type="number" min="1" value="1" inputmode="numeric" aria-label="Quantity" data-qs-qty>' +
                '<button type="button" data-qs-step="1" aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<button type="submit" class="btn btn-primary btn-lg quick-shop-add" data-qs-add>' + esc(LABEL_ADD) + '</button>' +
            '</div>' +
            '<p class="quick-shop-note" data-qs-note hidden></p>' +
            '<p class="quick-shop-error" role="alert" data-qs-error hidden></p>' +
          '</form>' +
          '<a class="quick-shop-details" data-qs-details href="' + esc(url) + '">View full details</a>' +
        '</div>' +
      '</div>';

    dialog.removeAttribute('aria-label');
    dialog.setAttribute('aria-labelledby', 'QuickShopTitle');
    update();
  }

  function currentVariant() {
    if (!state) return null;
    var sel = state.selected;
    return (state.product.variants || []).filter(function (v) {
      return v.options.every(function (val, i) { return val === sel[i]; });
    })[0] || null;
  }

  function setImage(src, alt) {
    var img = body.querySelector('[data-qs-image]');
    if (!img || !src) return;
    img.src = sized(src, 900);
    img.alt = alt || state.product.title;
    body.querySelectorAll('[data-qs-thumb]').forEach(function (t) {
      t.setAttribute('aria-current', t.getAttribute('data-qs-thumb').split('?')[0] === String(src).split('?')[0] ? 'true' : 'false');
    });
  }

  function update() {
    if (!state) return;
    var product = state.product;
    var variant = currentVariant();
    var price = variant ? variant.price : product.price;
    var compare = variant ? variant.compare_at_price : product.compare_at_price;

    body.querySelector('[data-qs-price]').textContent = formatMoney(price);
    var compareEl = body.querySelector('[data-qs-compare]');
    if (compare && compare > price) {
      compareEl.textContent = formatMoney(compare);
      compareEl.hidden = false;
    } else {
      compareEl.hidden = true;
    }

    state.selected.forEach(function (val, i) {
      var label = body.querySelector('[data-qs-selected="' + i + '"]');
      if (label) label.textContent = val;
    });

    // Grey out values that have no available variant with the other current choices.
    body.querySelectorAll('[data-qs-option]').forEach(function (input) {
      var i = Number(input.getAttribute('data-qs-option'));
      var available = (product.variants || []).some(function (v) {
        return v.available && v.options[i] === input.value && v.options.every(function (val, k) {
          return k === i || val === state.selected[k];
        });
      });
      var label = body.querySelector('label[for="' + input.id + '"]');
      if (label) label.classList.toggle('is-unavailable', !available);
    });

    var add = body.querySelector('[data-qs-add]');
    var note = body.querySelector('[data-qs-note]');
    var isCustom = !!variant && variant.options.indexOf(CUSTOM_VALUE) > -1;
    note.hidden = true;
    if (!variant) {
      add.disabled = true;
      add.textContent = LABEL_UNAVAILABLE;
    } else if (!variant.available) {
      add.disabled = true;
      add.textContent = LABEL_SOLD_OUT;
    } else if (isCustom) {
      add.disabled = true;
      add.textContent = LABEL_ADD;
      note.innerHTML = 'Custom sizes are entered on the product page. <a href="' + esc(withVariant(state.url, variant.id)) + '">Choose your size</a>';
      note.hidden = false;
    } else {
      add.disabled = false;
      add.textContent = LABEL_ADD;
    }

    var details = body.querySelector('[data-qs-details]');
    if (details) details.href = variant ? withVariant(state.url, variant.id) : state.url;

    var image = (variant && variant.featured_image) || null;
    setImage(image ? image.src : product.featured_image, image ? image.alt : product.title);
    body.querySelector('[data-qs-error]').hidden = true;
  }

  /* ---------- interactions inside the popup ---------- */
  body.addEventListener('change', function (e) {
    var input = e.target.closest('[data-qs-option]');
    if (!input || !state) return;
    state.selected[Number(input.getAttribute('data-qs-option'))] = input.value;
    update();
  });

  body.addEventListener('click', function (e) {
    var step = e.target.closest('[data-qs-step]');
    if (step) {
      var qty = body.querySelector('[data-qs-qty]');
      var next = Math.max(1, (parseInt(qty.value, 10) || 1) + Number(step.getAttribute('data-qs-step')));
      qty.value = next;
      return;
    }
    var thumb = e.target.closest('[data-qs-thumb]');
    if (thumb) setImage(thumb.getAttribute('data-qs-thumb'), state && state.product.title);
  });

  body.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-qs-form]');
    if (!form) return;
    e.preventDefault();
    var variant = currentVariant();
    var add = body.querySelector('[data-qs-add]');
    if (!variant || add.disabled) return;
    var error = body.querySelector('[data-qs-error]');
    var qtyInput = body.querySelector('[data-qs-qty]');
    var qty = parseInt(qtyInput.value, 10);
    // 0 or an empty box is refused (it used to add 1 silently).
    if (isNaN(qty) || qty < 1) {
      error.textContent = 'Please enter a quantity of 1 or more.';
      error.hidden = false;
      qtyInput.focus();
      return;
    }

    if (!window.Moonrise || !window.Moonrise.cart) {
      window.location.href = withVariant(state.url, variant.id);
      return;
    }

    error.hidden = true;
    add.disabled = true;
    add.textContent = 'Adding…';
    window.Moonrise.cart.add(variant.id, qty)
      .then(function () {
        dialog.close();
        if (window.Moonrise.openCartDrawer) window.Moonrise.openCartDrawer();
      })
      .catch(function (err) {
        error.textContent = (err && (err.description || err.message)) || 'This item could not be added. Please try again.';
        error.hidden = false;
        add.disabled = false;
        add.textContent = LABEL_ADD;
      });
  });

  window.Moonrise = window.Moonrise || {};
  window.Moonrise.quickShop = { open: open };
})();
